import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import recoveryIllustration from "@/assets/froppio-password-reset.svg";
import { authService } from "@/core/auth/services/authService";
import { trackEvent } from "@/core/telemetry/telemetry";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { Page, PageContent, PageFooter } from "@/shared/components/ui/Page";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { SetupHeader } from "@/shared/components/ui/SetupHeader";
import { EmailConfirmationIntro } from "../components/EmailConfirmationIntro";
import { EntryHeader } from "../components/EntryHeader";

export function PasswordRecoveryPage() {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    if (!resendCooldown) return;
    const timeout = window.setTimeout(() => setResendCooldown(false), 60_000);
    return () => window.clearTimeout(timeout);
  }, [resendCooldown]);

  const requestReset = async (address: string) => {
    if (submitting.current) return false;
    submitting.current = true;
    setError("");
    setNotice("");
    setLoading(true);
    try {
      await authService.requestPasswordReset(address);
      trackEvent("auth.password_reset_requested", { provider: authService.kind });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't send the reset link. Please try again.");
      return false;
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (await requestReset(address)) {
      setEmail(address);
      setSent(true);
      setResendCooldown(true);
    }
  };

  const handleResend = async () => {
    if (resendCooldown) return;
    setResendCooldown(true);
    if (await requestReset(email)) setNotice("Reset link requested. Check your inbox.");
  };

  if (sent) {
    return (
      <Page>
        <SetupHeader title="Check your email" titleSize="welcome" centered />
        <PageContent className="flex flex-col [scrollbar-gutter:auto]">
          <EmailConfirmationIntro>
            <p>
              If an account exists for
              <strong className="block break-all text-foreground" data-telemetry-mask>{email}</strong>
              you'll receive a link to choose a new password.
            </p>
            {error && <ErrorState message={error} />}
            {notice && <p role="status" className="text-primary">{notice}</p>}
            <SectionAction onClick={handleResend} disabled={loading || resendCooldown}>
              {loading ? "Sending..." : resendCooldown ? "Resend available in a minute" : "Resend reset link"}
            </SectionAction>
          </EmailConfirmationIntro>
        </PageContent>
        <PageFooter narrow>
          <Button asChild size="touch">
            <Link to="/login" state={location.state}>Sign in</Link>
          </Button>
        </PageFooter>
      </Page>
    );
  }

  return (
    <FormPage
      header={null}
      narrow
      contentClassName="flex min-h-full flex-col justify-center space-y-0 py-6"
      onSubmit={handleSubmit}
      actions={
        <Button type="submit" size="touch" disabled={loading}>
          {loading ? "Sending..." : "Send reset link"}
        </Button>
      }
    >
      <EntryHeader
        title="Forgot your password?"
        illustration={recoveryIllustration}
        illustrationAlt="Froppio holding a password reset envelope"
      />
      <div className="space-y-6" aria-busy={loading}>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Enter your account email and we'll send you a link to choose a new password.
        </p>
        {error && <ErrorState message={error} />}
        <FormField id="recovery-email" label="Email">
          <Input name="email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} readOnly={loading} />
        </FormField>
      </div>
      <p className="mt-2 text-center">
        <SectionAction asChild>
          <Link to="/login" state={location.state}>Sign in</Link>
        </SectionAction>
      </p>
    </FormPage>
  );
}
